import type { TyreVehicleKind } from '@chukua/common';
import { Alert } from 'antd';
import { CircleDot, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { VehicleTyreDiagram } from '../components/VehicleTyreDiagram';
import { VehicleTyreMoveModal } from '../components/VehicleTyreMoveModal';
import { VehicleTyreTable } from '../components/VehicleTyreTable';
import { useCanManageTyres } from '../hooks/use-can-manage-tyres';
import { useVehicleTyreActions } from '../hooks/use-vehicle-tyre-actions';
import { useVehicleTyreLayout } from '../hooks/use-vehicle-tyre-layout';

interface VehicleTyresTabProps {
  vehicleKind: TyreVehicleKind;
  vehicleId: string;
}

/**
 * The Tyres tab of a truck's or trailer's own page: its current tyre layout, every position with the tyre in it, beside the vehicle in 3D.
 * Fitting, replacing and rotating start from a position here, where the vehicle and the position are known.
 */
export function VehicleTyresTab({ vehicleKind, vehicleId }: VehicleTyresTabProps) {
  const { state, reload } = useVehicleTyreLayout(vehicleKind, vehicleId);
  const canManage = useCanManageTyres();
  const [selected, setSelected] = useState<string | null>(null);
  const actions = useVehicleTyreActions({ vehicleKind, vehicleId, layout: state.status === 'loaded' ? state.layout : null, reload });

  if (state.status === 'loading') return <TableSkeleton label="Loading tyres" />;
  if (state.status === 'forbidden') return <EmptyState icon={ShieldAlert} title="No access" description="You can't see this vehicle's tyres." />;
  if (state.status === 'error')
    return <EmptyState icon={ShieldAlert} title="Couldn't load the tyres" description="Something went wrong loading them. Try reloading the page." />;
  if (state.status === 'no_layout') {
    return <EmptyState icon={CircleDot} title="No tyre layout" description={`${state.message} An admin draws one under Reference data.`} />;
  }

  const { layout } = state;

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-muted">
        {layout.registration} · layout {layout.layoutCode}. Click a position, or a tyre on the vehicle, to find it.
      </p>
      {layout.slots.some((slot) => slot.isRetired) && (
        <Alert
          type="warning"
          showIcon
          title="A position has been removed from this layout but still holds a tyre. Remove or rotate that tyre to move it onto the layout."
        />
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <VehicleTyreTable
          slots={layout.slots}
          canManage={canManage}
          selectedCode={selected}
          onSelect={setSelected}
          onFit={(slot) => actions.open('fit', slot)}
          onReplace={(slot) => actions.open('replace', slot)}
          onRotate={(slot) => actions.open('rotate', slot)}
        />
        <VehicleTyreDiagram layout={layout} selectedCode={selected} onSelect={setSelected} />
      </div>
      <VehicleTyreMoveModal actions={actions} />
    </div>
  );
}
